import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const ColorPickerContainer = styled.div`
  margin-top: 20px;
  padding: 15px;
  background-color: #f0f0f0;
  border-radius: 8px;
`;

const ElementList = styled.div`
  margin-bottom: 15px;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const ElementItem = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:hover {
    background-color: #e0e0e0;
  }
  
  &.selected {
    background-color: #d0d0d0;
  }
`;

const ColorInput = styled.input`
  margin-left: 10px;
`;

const NoElements = styled.div`
  padding: 10px;
  color: #666;
  text-align: center;
`;

const ColorPicker = ({ svgCode, setSvgCode }) => {
  const [elements, setElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedColor, setSelectedColor] = useState('#000000');

  // Parse SVG and extract elements with fill or stroke attributes
  useEffect(() => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgCode, 'image/svg+xml');
      
      // Get all elements with fill or stroke attributes
      const elementsWithColor = [];
      const svgElements = doc.querySelectorAll('*');
      
      svgElements.forEach((el, index) => {
        if (el.tagName !== 'svg' && (el.hasAttribute('fill') || el.hasAttribute('stroke'))) {
          const fill = el.getAttribute('fill');
          const stroke = el.getAttribute('stroke');
          
          elementsWithColor.push({
            id: index,
            tag: el.tagName,
            fill: fill !== 'none' ? fill : null,
            stroke: stroke !== 'none' ? stroke : null,
            element: el
          });
        }
      });
      
      setElements(elementsWithColor);
      setSelectedElement(null);
    } catch (err) {
      console.error('Error parsing SVG:', err);
    }
  }, [svgCode]);

  const handleElementSelect = (element) => {
    setSelectedElement(element);
    if (element.fill) {
      setSelectedColor(element.fill);
    } else if (element.stroke) {
      setSelectedColor(element.stroke);
    }
  };

  const handleColorChange = (e) => {
    const newColor = e.target.value;
    setSelectedColor(newColor);
    
    if (!selectedElement) return;
    
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgCode, 'image/svg+xml');
      
      // Find the element in the parsed document
      const svgElements = doc.querySelectorAll('*');
      const targetElement = svgElements[selectedElement.id];
      
      if (targetElement) {
        // Update the color based on what the element has
        if (selectedElement.fill) {
          targetElement.setAttribute('fill', newColor);
        } else if (selectedElement.stroke) {
          targetElement.setAttribute('stroke', newColor);
        }
        
        // Update the SVG code
        setSvgCode(new XMLSerializer().serializeToString(doc));
      }
    } catch (err) {
      console.error('Error updating color:', err);
    }
  };

  return (
    <ColorPickerContainer>
      <h3>Color Editor</h3>
      <ElementList>
        {elements.length > 0 ? (
          elements.map((element) => (
            <ElementItem 
              key={element.id}
              className={selectedElement && selectedElement.id === element.id ? 'selected' : ''}
              onClick={() => handleElementSelect(element)}
            >
              <span>{element.tag} {element.fill ? `(fill)` : `(stroke)`}</span>
              <div 
                style={{ 
                  width: '20px', 
                  height: '20px', 
                  backgroundColor: element.fill || element.stroke || 'transparent',
                  border: '1px solid #ccc'
                }} 
              />
            </ElementItem>
          ))
        ) : (
          <NoElements>No colorable elements found</NoElements>
        )}
      </ElementList>
      
      {selectedElement && (
        <div>
          <label>
            Color:
            <ColorInput 
              type="color" 
              value={selectedColor}
              onChange={handleColorChange}
            />
          </label>
        </div>
      )}
    </ColorPickerContainer>
  );
};

export default ColorPicker; 